<template>
  <div class="country-selection-list-fi">
    <grid-container-fi v-show="continents?.length">
      <div class="country-selection-list-fi__search-container">
        <div
          :class="{
            'country-selection-list-fi__search': true,
            'country-selection-list-fi__search--mobile-search-activated':
              mobileSearchActivated && !isSM,
          }"
        >
          <span class="country-selection-list-fi__search-title">
            {{ $dict.get('global-Labels.Country-Selection.Search-Title') }}
          </span>
          <span class="country-selection-list-fi__search-input-wrapper">
            <text-input-fi
              ref="country-search"
              v-model.trim="search"
              name="country-search"
              class="country-selection-list-fi__search-input"
              :label="$dict.get('global-Labels.Country-Selection.Search-Input-Label')"
              :icon="mobileSearchActivated && !isSM ? 'close' : 'search'"
              :clear="true"
              type="search"
              :large="isSM"
              data-test="country-search-input"
              @icon-clicked="mobileSearchActivated = false"
              @update:model-value="change"
            />
            <icon-fi
              v-if="!mobileSearchActivated"
              icon="search"
              class="country-selection-list-fi__search-icon-mobile"
              @click="mobileSearchActivated = !mobileSearchActivated"
            />
          </span>
        </div>

        <!-- eslint-disable vue/no-v-html -->
        <div
          v-show="showNoCountryFoundHint"
          class="country-selection-list-fi__no-result"
          v-html="
            $dict.get('global-Labels.Country-Selection.Search-No-Results', [
              `<span id='search-term'></span>`,
            ]) || `<span class='fi-hidden' id='search-term'></span>`
          "
        />
        <!-- eslint-enable vue/no-v-html -->
        <teleport
          v-if="isMounted && showNoCountryFoundHint"
          to="#search-term"
          >{{ search }}</teleport
        >
      </div>
    </grid-container-fi>
    <grid-container-fi v-if="continents?.length && !showNoCountryFoundHint">
      <div class="country-selection-list-fi__container">
        <ul
          ref="continents"
          class="country-selection-list-fi__list"
        >
          <template
            v-for="(continent, index) in continents"
            :key="continent.displayName"
          >
            <li
              v-if="filteredCountries(continent.countries)?.length > 0"
              class="country-selection-list-fi__continent-container"
              :class="index < continents.length - 1 ? 'lg:fi-col-span-12' : 'lg:fi-col-span-6'"
            >
              <div class="country-selection-list-fi__continent-header">
                {{ continent.displayName }}
              </div>

              <grid-container-fi :container="false">
                <template
                  v-for="country in filteredCountries(continent.countries)"
                  :key="country.displayName"
                >
                  <div class="country-selection-list-fi__continent-wrapper">
                    <country-flag-fi
                      v-if="country?.value && country.value !== 'en-ZZ'"
                      :country="country.value.toLowerCase()"
                      class="country-selection-list-fi__icon"
                    />
                    <icon-fi
                      v-else
                      size="0_8"
                      icon="globe"
                      class="country-selection-list-fi__icon country-selection-list-fi__icon--svg"
                    />
                    <template v-if="country.value !== 'en-ZZ'">
                      <span
                        v-if="country?.title"
                        class="country-selection-list-fi__list-title"
                      >
                        fischer {{ country.title }}
                      </span>
                      <br class="country-selection-list-fi__break" />
                    </template>
                    <span
                      v-if="
                        country?.localizations?.length &&
                        (countryUrlKey ? country[countryUrlKey]?.url : country.link?.url)
                      "
                      :class="{
                        'country-selection-list-fi__languages': true,
                        'country-selection-list-fi__languages--international':
                          country.value === 'en-ZZ',
                      }"
                    >
                      <component
                        :is="redirectLayer ? 'span' : 'a'"
                        v-for="language in country.localizations"
                        :key="language.title"
                        :href="
                          redirectLayer || onlyEmitOnClick ? null : countryUrl(country, language)
                        "
                        :class="{
                          'country-selection-list-fi__url': true,
                          'country-selection-list-fi__url--redirect': redirectLayer,
                        }"
                        @click="onCountryClick(country, language)"
                      >
                        {{ language.name }}
                      </component>
                    </span>
                  </div>
                </template>
              </grid-container-fi>
            </li>
          </template>
        </ul>
      </div>
    </grid-container-fi>

    <div
      v-if="redirectLayer"
      :class="{
        'country-selection-list-fi__redirect-layer': true,
        'country-selection-list-fi__redirect-layer--active': showRedirectLayer,
      }"
    >
      <grid-container-fi>
        <div class="country-selection-list-fi__redirect-content">
          <div class="country-selection-list-fi__redirect-text-content">
            <div class="country-selection-list-fi__redirect-headline">
              {{ fields.redirectLayerHeadline?.value }}
            </div>
            <!-- eslint-disable vue/no-v-html -->
            <div
              class="country-selection-list-fi__redirect-description"
              v-html="
                getPreparedPlaceholderValue(
                  fields,
                  'redirectLayerDescription',
                  activeCountry?.title,
                )
              "
            />
            <!-- eslint-enable vue/no-v-html -->
          </div>
          <div class="country-selection-list-fi__redirect-controls">
            <div class="country-selection-list-fi__redirect-disagree">
              <button-fi
                :class="{
                  'button-fi__tertiary button-fi__xs-streched': true,
                  'button-fi--large': isSM,
                }"
                type="button"
                @click="closeRedirectLayer"
              >
                {{ fields.redirectLayerBtnDisagree?.value }}
              </button-fi>
            </div>
            <div class="country-selection-list-fi__redirect-agree">
              <button-fi
                :class="{
                  'button-fi__primary button-fi__xs-streched': true,
                  'button-fi--large': isSM,
                }"
                type="button"
                @click="redirectToCountry"
              >
                {{ fields.redirectLayerBtnAgree?.value }}
              </button-fi>
            </div>
          </div>
        </div>
      </grid-container-fi>
    </div>
  </div>
</template>

<script>
import { captureException } from '@sentry/browser';
import { ButtonFi, CountryFlagFi, IconFi, TextInputFi } from 'atoms';
import axios from 'axios';
import GridContainerFi from 'components/grid-fi/GridContainerFi';
import breakpointMixin from 'mixins/breakpointMixin.vue';
import { getPreparedPlaceholderValue } from 'utils/fieldTransformations';

export default {
  name: 'CountrySelectionListFi',
  components: {
    ButtonFi,
    TextInputFi,
    CountryFlagFi,
    GridContainerFi,
    IconFi,
  },
  mixins: [breakpointMixin],
  inject: ['fields'],
  props: {
    countriesFilter: {
      type: String,
      default: null,
    },
    countryUrlKey: {
      type: String,
      default: null,
    },
    redirectLayer: {
      type: Boolean,
      default: false,
    },
    onlyEmitOnClick: Boolean,
  },
  emits: ['country-clicked'],
  data() {
    return {
      countryData: null,
      filteredCountryData: null,
      search: '',
      mobileSearchActivated: false,
      showRedirectLayer: false,
      activeCountry: null,
      isMounted: false,
    };
  },
  computed: {
    title() {
      return this.$dict.get('global-Labels.Country-Selection.Title');
    },
    subtitle() {
      return this.$dict.get('global-Labels.Country-Selection.Subtitle');
    },
    description() {
      return this.$dict.get('global-Labels.Country-Selection.Description');
    },
    countryList() {
      return this.filteredCountryData || this.countryData;
    },
    continents() {
      return this.countryList?.continents?.continents;
    },
    showNoCountryFoundHint() {
      return this.filteredCountryData?.continents.continents.every(
        (continent) => !continent.countries || continent.countries?.length === 0,
      );
    },
  },
  mounted() {
    this.isMounted = true;
  },
  async created() {
    return this.fetchCountries();
  },
  serverPrefetch() {
    return this.fetchCountries();
  },
  methods: {
    getPreparedPlaceholderValue,
    getApi() {
      if (IS_SHOWROOM) {
        return '/__mocks__/sitecoreApi/api/i18n/countries.json';
      }
      const path = '/api/i18n/countries';
      if (IS_SSR) {
        return `${this.$fischer.hostUrlBase()}${path}`;
      }
      return path;
    },
    async fetchCountries() {
      if (this.countryData) {
        return;
      }
      try {
        const api = this.getApi();
        const params = { language: this.$fischer.siteLanguage() };
        const { data } = await axios.get(api, IS_SHOWROOM ? null : { params });

        this.countryData = data;
      } catch (error) {
        console.error(error);
        captureException(error);
      }
    },
    countryUrl(country, language) {
      if (this.countryUrlKey && country[this.countryUrlKey]?.url) {
        return country[this.countryUrlKey]?.url;
      }

      return `${country.link?.url}/${language.iso_Code.toLowerCase()}`;
    },
    filteredCountries(countries) {
      if (this.countriesFilter) {
        return countries?.filter((c) => c[this.countriesFilter]?.url);
      }
      return countries;
    },
    filterCountries() {
      this.filteredCountryData = JSON.parse(JSON.stringify(this.countryData));
      this.filteredCountryData.continents.continents.forEach((continent) => {
        continent.countries = continent.countries?.filter(
          (c) =>
            c.displayName.toLowerCase().includes(this.search.toLowerCase()) ||
            c.title.toLowerCase().includes(this.search.toLowerCase()) ||
            c.localizations.some((l) => l.name.toLowerCase().includes(this.search.toLowerCase())),
        );
      });
    },
    change() {
      this.filterCountries();
    },
    onCountryClick(country, language) {
      this.activeCountry = country;
      this.showRedirectLayer = true;
      this.$emit('country-clicked', country, language);
    },
    closeRedirectLayer() {
      this.showRedirectLayer = false;
    },
    redirectToCountry() {
      let redirectUrl = this.activeCountry.link?.url;
      if (this.countryUrlKey && this.activeCountry[this.countryUrlKey]?.url) {
        redirectUrl = this.activeCountry[this.countryUrlKey].url;
      }
      window.location.href = redirectUrl;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './country-selection-list-fi.scss';
</style>
